import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { apiClient } from '../utils/apiClient';
import ConfirmDialog from '../components/ConfirmDialog';
import PromptDialog from '../components/PromptDialog';
import PageShell from '../components/PageShell';
import toast from 'react-hot-toast';
export const SearchPage = () => {
    // 検索状態
    const [searchQuery, setSearchQuery] = useState('');
    const [searchGroups, setSearchGroups] = useState([
        { id: '1', operator: 'AND', conditions: [] }
    ]);
    const [selectedDataSources, setSelectedDataSources] = useState(['agents']);
    const [filters, setFilters] = useState({});
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // 保存済み検索
    const [savedSearches, setSavedSearches] = useState([]);
    const [searchToDelete, setSearchToDelete] = useState(null);
    const [deletingSearch, setDeletingSearch] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [showSavedSearches, setShowSavedSearches] = useState(false);
    const [savingSearch, setSavingSearch] = useState(false);
    // クイックフィルタ
    const [quickFilters, setQuickFilters] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    // 高度検索UI表示制御
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [showRangeFilter, setShowRangeFilter] = useState(false);
    // データソースオプション
    const dataSourceOptions = [
        { value: 'agents', label: 'エージェント', icon: '🤖' },
        { value: 'notifications', label: '通知', icon: '📢' },
        { value: 'logs', label: 'ログ', icon: '📝' },
        { value: 'workflows', label: 'ワークフロー', icon: '⚙️' },
        { value: 'documents', label: 'ドキュメント', icon: '📄' }
    ];
    // フィールドオプション
    const fieldOptions = [
        { value: 'title', label: 'タイトル', type: 'text' },
        { value: 'description', label: '説明', type: 'text' },
        { value: 'category', label: 'カテゴリ', type: 'select' },
        { value: 'status', label: 'ステータス', type: 'select' },
        { value: 'priority', label: '優先度', type: 'select' },
        { value: 'created_at', label: '作成日', type: 'date' },
        { value: 'updated_at', label: '更新日', type: 'date' }
    ];
    // 演算子オプション
    const operatorOptions = {
        text: [
            { value: 'contains', label: '含む' },
            { value: 'equals', label: '等しい' },
            { value: 'starts_with', label: '始まる' },
            { value: 'ends_with', label: '終わる' }
        ],
        date: [
            { value: 'equals', label: '等しい' },
            { value: 'after', label: '以降' },
            { value: 'before', label: '以前' },
            { value: 'between', label: '期間内' }
        ],
        number: [
            { value: 'equals', label: '等しい' },
            { value: 'greater_than', label: '大きい' },
            { value: 'less_than', label: '小さい' },
            { value: 'between', label: '範囲内' }
        ],
        select: [
            { value: 'equals', label: '等しい' },
            { value: 'in', label: 'いずれか' }
        ]
    };
    // 初期データ読み込み
    useEffect(() => {
        loadSavedSearches();
        loadQuickFilters();
    }, []);
    // 保存済み検索読み込み
    const loadSavedSearches = async () => {
        try {
            const response = await apiClient.search.saved.list();
            setSavedSearches(Array.isArray(response.data) ? response.data : []);
        }
        catch (error) {
            console.error('Failed to load saved searches:', error);
        }
    };
    // クイックフィルタ読み込み
    const loadQuickFilters = async () => {
        try {
            const response = await apiClient.search.filters.list();
            setQuickFilters(Array.isArray(response.data) ? response.data : []);
        }
        catch (error) {
            console.error('Failed to load quick filters:', error);
        }
    };
    // 検索実行
    const handleSearch = async () => {
        if (!searchQuery.trim() && searchGroups.every(g => g.conditions.length === 0)) {
            setError('検索クエリまたは条件を入力してください');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const request = {
                query: searchQuery,
                data_sources: selectedDataSources,
                filters: {
                    ...filters,
                    // 高度検索条件を統合
                    ...buildAdvancedFilters()
                },
                page: 1,
                limit: 20
            };
            const response = await apiClient.search.unified(request);
            // レスポンスの安全な処理
            if (response.data) {
                const searchResults = response.data;
                // 結果配列のnullチェック
                if (searchResults.results === null || searchResults.results === undefined) {
                    searchResults.results = [];
                }
                setResults(searchResults);
            }
            else {
                setError('検索結果の取得に失敗しました');
            }
        }
        catch (error) {
            setError('検索に失敗しました');
            console.error('Search error:', error);
        }
        finally {
            setLoading(false);
        }
    };
    // 高度検索条件構築
    const buildAdvancedFilters = () => {
        const advancedFilters = {};
        // 検索グループから条件を構築
        searchGroups.forEach(group => {
            group.conditions.forEach(condition => {
                if (condition.value.trim()) {
                    // 実際の実装では、条件をより詳細に処理
                    if (condition.field === 'category') {
                        advancedFilters.categories = advancedFilters.categories || [];
                        advancedFilters.categories.push(condition.value);
                    }
                    else if (condition.field === 'status') {
                        advancedFilters.status = advancedFilters.status || [];
                        advancedFilters.status.push(condition.value);
                    }
                    else if (condition.field === 'priority') {
                        advancedFilters.priority = advancedFilters.priority || [];
                        advancedFilters.priority.push(condition.value);
                    }
                    else if (condition.field === 'created_at' || condition.field === 'updated_at') {
                        if (condition.operator === 'after' || condition.operator === 'before') {
                            advancedFilters.date_range = advancedFilters.date_range || { from: '', to: '' };
                            if (condition.operator === 'after') {
                                advancedFilters.date_range.from = condition.value;
                            }
                            else {
                                advancedFilters.date_range.to = condition.value;
                            }
                        }
                    }
                }
            });
        });
        return advancedFilters;
    };
    // 検索条件追加
    const addSearchCondition = (groupId) => {
        const newCondition = {
            id: Date.now().toString(),
            field: 'title',
            operator: 'contains',
            value: '',
            type: 'text'
        };
        setSearchGroups(groups => groups.map(group => group.id === groupId
            ? { ...group, conditions: [...group.conditions, newCondition] }
            : group));
    };
    // 検索条件削除
    const removeSearchCondition = (groupId, conditionId) => {
        setSearchGroups(groups => groups.map(group => group.id === groupId
            ? { ...group, conditions: group.conditions.filter(c => c.id !== conditionId) }
            : group));
    };
    // 検索条件更新
    const updateSearchCondition = (groupId, conditionId, updates) => {
        setSearchGroups(groups => groups.map(group => group.id === groupId
            ? {
                ...group,
                conditions: group.conditions.map(condition => condition.id === conditionId
                    ? { ...condition, ...updates }
                    : condition)
            }
            : group));
    };
    // 検索グループ追加
    const addSearchGroup = () => {
        const newGroup = {
            id: Date.now().toString(),
            operator: 'AND',
            conditions: []
        };
        setSearchGroups([...searchGroups, newGroup]);
    };
    // 検索グループ削除
    const removeSearchGroup = (groupId) => {
        setSearchGroups(groups => groups.filter(g => g.id !== groupId));
    };
    // 検索保存ダイアログを開く
    const saveSearch = () => {
        setShowSaveDialog(true);
    };
    // 検索保存の確定
    const handleSaveSearchSubmit = async (name, description) => {
        setSavingSearch(true);
        try {
            const savedSearch = await apiClient.search.saved.create({
                name,
                description: description || '',
                query: {
                    query: searchQuery,
                    data_sources: selectedDataSources,
                    filters: buildAdvancedFilters(),
                    page: 1,
                    limit: 20
                }
            });
            setSavedSearches([...savedSearches, savedSearch.data]);
            setShowSaveDialog(false);
            toast.success('検索が保存されました');
        }
        catch (error) {
            toast.error('検索の保存に失敗しました');
            console.error('Save search error:', error);
        }
        finally {
            setSavingSearch(false);
        }
    };
    // 保存済み検索実行
    const executeSavedSearch = async (savedSearch) => {
        if (!savedSearch.id)
            return;
        setLoading(true);
        try {
            const response = await apiClient.search.saved.execute(savedSearch.id);
            // レスポンスの安全な処理
            if (response.data) {
                const searchResults = response.data;
                // 結果配列のnullチェック
                if (searchResults.results === null || searchResults.results === undefined) {
                    searchResults.results = [];
                }
                setResults(searchResults);
                // UIを更新
                setSearchQuery(savedSearch.query.query);
                setSelectedDataSources(savedSearch.query.data_sources || []);
                setFilters(savedSearch.query.filters || {});
            }
            else {
                setError('保存済み検索の実行に失敗しました');
            }
        }
        catch (error) {
            setError('保存済み検索の実行に失敗しました');
            console.error('Execute saved search error:', error);
        }
        finally {
            setLoading(false);
        }
    };
    // 保存済み検索削除
    const handleConfirmDeleteSearch = async () => {
        if (!searchToDelete?.id)
            return;
        setDeletingSearch(true);
        try {
            await apiClient.search.saved.delete(searchToDelete.id);
            setSavedSearches(prev => prev.filter(s => s.id !== searchToDelete.id));
            setSearchToDelete(null);
        }
        catch (error) {
            console.error('Delete saved search error:', error);
            setError('保存済み検索の削除に失敗しました');
        }
        finally {
            setDeletingSearch(false);
        }
    };
    // 検索推奨取得
    const getSuggestions = async (query) => {
        if (query.length < 2) {
            setSuggestions([]);
            return;
        }
        try {
            const response = await apiClient.search.suggestions.get(query, 5);
            setSuggestions(Array.isArray(response.data) ? response.data : []);
        }
        catch (error) {
            console.error('Failed to get suggestions:', error);
        }
    };
    return (_jsxs(PageShell, { title: "\u9AD8\u5EA6\u691C\u7D22\u30B7\u30B9\u30C6\u30E0", icon: _jsx("span", { children: "\uD83D\uDD0D" }), actions: _jsxs(_Fragment, { children: [_jsxs("button", { onClick: () => setShowSavedSearches(!showSavedSearches), className: "px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-500", children: ["\uD83D\uDCC1 \u4FDD\u5B58\u6E08\u307F\u691C\u7D22 (", savedSearches.length, ")"] }), _jsx("button", { onClick: () => setShowAdvancedSearch(!showAdvancedSearch), className: "px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-500", children: "\u2699\uFE0F \u9AD8\u5EA6\u691C\u7D22" })] }), children: [_jsxs("div", { className: "grid grid-cols-1 lg:grid-cols-3 gap-6", children: [_jsxs("div", { className: "lg:col-span-2", children: [_jsxs("div", { className: "bg-white rounded-lg shadow-sm p-6 mb-6", children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900 mb-4", children: "\u57FA\u672C\u691C\u7D22" }), _jsxs("div", { className: "mb-4", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u691C\u7D22\u30AF\u30A8\u30EA" }), _jsxs("div", { className: "relative", children: [_jsx("input", { type: "text", value: searchQuery, onChange: (e) => {
                                                            setSearchQuery(e.target.value);
                                                            getSuggestions(e.target.value);
                                                        }, placeholder: "\u691C\u7D22\u3057\u305F\u3044\u30AD\u30FC\u30EF\u30FC\u30C9\u3092\u5165\u529B...", className: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" }), suggestions.length > 0 && (_jsx("div", { className: "absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1", children: suggestions.map((suggestion, index) => (_jsxs("button", { onClick: () => {
                                                                setSearchQuery(suggestion.query);
                                                                setSuggestions([]);
                                                            }, className: "w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0", children: [_jsx("span", { className: "text-gray-900", children: suggestion.query }), _jsxs("span", { className: "text-gray-500 text-sm ml-2", children: ["(", suggestion.type, ")"] })] }, index))) }))] })] }), _jsxs("div", { className: "mb-4", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u691C\u7D22\u5BFE\u8C61\u30C7\u30FC\u30BF\u30BD\u30FC\u30B9" }), _jsx("div", { className: "grid grid-cols-2 md:grid-cols-3 gap-2", children: dataSourceOptions.map((option) => (_jsxs("label", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "checkbox", checked: selectedDataSources.includes(option.value), onChange: (e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedDataSources([...selectedDataSources, option.value]);
                                                                }
                                                                else {
                                                                    setSelectedDataSources(selectedDataSources.filter(ds => ds !== option.value));
                                                                }
                                                            }, className: "rounded text-blue-600 focus:ring-blue-500" }), _jsxs("span", { className: "text-sm text-gray-700", children: [option.icon, " ", option.label] })] }, option.value))) })] }), _jsxs("div", { className: "flex items-center space-x-4", children: [_jsx("button", { onClick: handleSearch, disabled: loading, className: "px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed", children: loading ? '検索中...' : '🔍 検索実行' }), _jsx("button", { onClick: saveSearch, disabled: savingSearch || !results, className: "px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed", children: savingSearch ? '保存中...' : '💾 検索保存' })] })] }), showAdvancedSearch && (_jsxs("div", { className: "bg-white rounded-lg shadow-sm p-6 mb-6", children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900 mb-4", children: "\u9AD8\u5EA6\u691C\u7D22\u6761\u4EF6" }), searchGroups.map((group, groupIndex) => (_jsxs("div", { className: "mb-6 p-4 border border-gray-200 rounded-lg", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("select", { value: group.operator, onChange: (e) => {
                                                            const newOperator = e.target.value;
                                                            setSearchGroups(groups => groups.map(g => g.id === group.id ? { ...g, operator: newOperator } : g));
                                                        }, className: "px-3 py-2 border border-gray-300 rounded-md text-sm", children: [_jsx("option", { value: "AND", children: "AND (\u3059\u3079\u3066)" }), _jsx("option", { value: "OR", children: "OR (\u3044\u305A\u308C\u304B)" })] }), _jsx("button", { onClick: () => removeSearchGroup(group.id), disabled: searchGroups.length === 1, className: "text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed", children: "\uD83D\uDDD1\uFE0F \u30B0\u30EB\u30FC\u30D7\u524A\u9664" })] }), group.conditions.map((condition) => (_jsxs("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4 mb-3", children: [_jsx("select", { value: condition.field, onChange: (e) => {
                                                            const field = fieldOptions.find(f => f.value === e.target.value);
                                                            updateSearchCondition(group.id, condition.id, {
                                                                field: e.target.value,
                                                                type: field?.type || 'text',
                                                                operator: operatorOptions[field?.type]?.[0]?.value || 'contains'
                                                            });
                                                        }, className: "px-3 py-2 border border-gray-300 rounded-md text-sm", children: fieldOptions.map((field) => (_jsx("option", { value: field.value, children: field.label }, field.value))) }), _jsx("select", { value: condition.operator, onChange: (e) => updateSearchCondition(group.id, condition.id, { operator: e.target.value }), className: "px-3 py-2 border border-gray-300 rounded-md text-sm", children: operatorOptions[condition.type]?.map((op) => (_jsx("option", { value: op.value, children: op.label }, op.value))) }), _jsx("input", { type: condition.type === 'date' ? 'date' : condition.type === 'number' ? 'number' : 'text', value: condition.value, onChange: (e) => updateSearchCondition(group.id, condition.id, { value: e.target.value }), className: "px-3 py-2 border border-gray-300 rounded-md text-sm", placeholder: "\u5024\u3092\u5165\u529B..." }), _jsx("button", { onClick: () => removeSearchCondition(group.id, condition.id), className: "text-red-600 hover:text-red-700 text-sm", children: "\uD83D\uDDD1\uFE0F \u524A\u9664" })] }, condition.id))), _jsx("button", { onClick: () => addSearchCondition(group.id), className: "text-blue-600 hover:text-blue-700 text-sm", children: "\u2795 \u6761\u4EF6\u8FFD\u52A0" })] }, group.id))), _jsx("button", { onClick: addSearchGroup, className: "text-green-600 hover:text-green-700 text-sm", children: "\u2795 \u6761\u4EF6\u30B0\u30EB\u30FC\u30D7\u8FFD\u52A0" })] })), error && (_jsx("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 mb-6", children: _jsx("p", { className: "text-red-800", children: error }) })), results && (_jsxs("div", { className: "bg-white rounded-lg shadow-sm p-6", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("h2", { className: "text-lg font-semibold text-gray-900", children: ["\u691C\u7D22\u7D50\u679C (", results.total_results, "\u4EF6)"] }), _jsxs("div", { className: "text-sm text-gray-500", children: ["\u5B9F\u884C\u6642\u9593: ", results.search_time, "ms"] })] }), !results.results || results.results.length === 0 ? (_jsx("div", { className: "text-center py-8 text-gray-500", children: "\u691C\u7D22\u7D50\u679C\u304C\u898B\u3064\u304B\u308A\u307E\u305B\u3093\u3067\u3057\u305F" })) : (_jsx("div", { className: "space-y-4", children: results.results.map((result, index) => (_jsx("div", { className: "border border-gray-200 rounded-lg p-4", children: _jsx("div", { className: "flex items-start justify-between", children: _jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: "flex items-center space-x-2 mb-2", children: [_jsxs("span", { className: "text-sm font-medium text-blue-600", children: [dataSourceOptions.find(ds => ds.value === result.data_source)?.icon, dataSourceOptions.find(ds => ds.value === result.data_source)?.label] }), _jsxs("span", { className: "text-sm text-gray-500", children: ["\u95A2\u9023\u5EA6: ", (result.relevance * 100).toFixed(1), "%"] })] }), _jsx("h3", { className: "font-medium text-gray-900 mb-2", children: result.title }), _jsx("p", { className: "text-sm text-gray-600 mb-2", children: result.description }), _jsxs("div", { className: "flex items-center space-x-4 text-xs text-gray-500", children: [_jsxs("span", { children: ["\u4F5C\u6210: ", new Date(result.created_at).toLocaleString()] }), _jsxs("span", { children: ["\u66F4\u65B0: ", new Date(result.updated_at).toLocaleString()] })] })] }) }) }, index))) })), results.total_results > results.limit && (_jsxs("div", { className: "flex items-center justify-between mt-6", children: [_jsxs("div", { className: "text-sm text-gray-500", children: [((results.page - 1) * results.limit) + 1, " - ", Math.min(results.page * results.limit, results.total_results), " / ", results.total_results, "\u4EF6"] }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("button", { disabled: results.page === 1, className: "px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50", children: "\u524D\u3078" }), _jsxs("span", { className: "text-sm text-gray-500", children: [results.page, " / ", results.total_pages] }), _jsx("button", { disabled: results.page >= results.total_pages, className: "px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50", children: "\u6B21\u3078" })] })] }))] }))] }), _jsxs("div", { className: "lg:col-span-1", children: [showSavedSearches && (_jsxs("div", { className: "bg-white rounded-lg shadow-sm p-6 mb-6", children: [_jsx("h3", { className: "text-lg font-semibold text-gray-900 mb-4", children: "\u4FDD\u5B58\u6E08\u307F\u691C\u7D22" }), savedSearches.length === 0 ? (_jsx("p", { className: "text-gray-500 text-sm", children: "\u4FDD\u5B58\u6E08\u307F\u691C\u7D22\u304C\u3042\u308A\u307E\u305B\u3093" })) : (_jsx("div", { className: "space-y-3", children: savedSearches.map((search) => (_jsx("div", { className: "border border-gray-200 rounded-lg p-3", children: _jsx("div", { className: "flex items-start justify-between", children: _jsxs("div", { className: "flex-1", children: [_jsx("h4", { className: "font-medium text-gray-900 text-sm", children: search.name }), search.description && (_jsx("p", { className: "text-xs text-gray-600 mt-1", children: search.description })), _jsxs("div", { className: "flex items-center space-x-2 mt-2", children: [_jsx("button", { onClick: () => executeSavedSearch(search), className: "text-xs text-blue-600 hover:text-blue-700", children: "\uD83D\uDD0D \u5B9F\u884C" }), _jsx("button", { onClick: () => setSearchToDelete(search), className: "text-xs text-red-600 hover:text-red-700", children: "\uD83D\uDDD1\uFE0F \u524A\u9664" })] })] }) }) }, search.id))) }))] })), _jsxs("div", { className: "bg-white rounded-lg shadow-sm p-6 mb-6", children: [_jsx("h3", { className: "text-lg font-semibold text-gray-900 mb-4", children: "\u30AF\u30A4\u30C3\u30AF\u30D5\u30A3\u30EB\u30BF" }), _jsxs("div", { className: "space-y-2", children: [_jsx("button", { className: "w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-md", children: "\uD83D\uDCC5 \u4ECA\u65E5\u4F5C\u6210" }), _jsx("button", { className: "w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-md", children: "\uD83D\uDCC5 \u4ECA\u9031\u4F5C\u6210" }), _jsx("button", { className: "w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-md", children: "\uD83D\uDD34 \u9AD8\u512A\u5148\u5EA6" }), _jsx("button", { className: "w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-md", children: "\u2705 \u5B8C\u4E86\u30B9\u30C6\u30FC\u30BF\u30B9" })] })] }), results && (_jsxs("div", { className: "bg-white rounded-lg shadow-sm p-6", children: [_jsx("h3", { className: "text-lg font-semibold text-gray-900 mb-4", children: "\u691C\u7D22\u7D71\u8A08" }), _jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "text-sm", children: [_jsx("span", { className: "text-gray-600", children: "\u30C7\u30FC\u30BF\u30BD\u30FC\u30B9\u5225:" }), _jsx("div", { className: "mt-1 space-y-1", children: Object.entries(results.aggregations?.data_sources || {}).map(([source, count]) => (_jsxs("div", { className: "flex justify-between", children: [_jsx("span", { children: dataSourceOptions.find(ds => ds.value === source)?.label }), _jsx("span", { children: count })] }, source))) })] }), _jsxs("div", { className: "text-sm", children: [_jsx("span", { className: "text-gray-600", children: "\u30AB\u30C6\u30B4\u30EA\u5225:" }), _jsx("div", { className: "mt-1 space-y-1", children: Object.entries(results.aggregations?.categories || {}).map(([category, count]) => (_jsxs("div", { className: "flex justify-between", children: [_jsx("span", { children: category }), _jsx("span", { children: count })] }, category))) })] })] })] }))] })] }), _jsx(ConfirmDialog, { isOpen: !!searchToDelete, onClose: () => setSearchToDelete(null), onConfirm: handleConfirmDeleteSearch, danger: true, busy: deletingSearch, title: "\u4FDD\u5B58\u6E08\u307F\u691C\u7D22\u306E\u524A\u9664", message: "\u3053\u306E\u691C\u7D22\u3092\u524A\u9664\u3057\u307E\u3059\u304B\uFF1F", confirmLabel: "\u524A\u9664" }), _jsx(PromptDialog, { isOpen: showSaveDialog, onClose: () => setShowSaveDialog(false), onSubmit: handleSaveSearchSubmit, busy: savingSearch, title: "\u691C\u7D22\u3092\u4FDD\u5B58", label: "\u691C\u7D22\u540D", placeholder: "\u691C\u7D22\u540D\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044", required: true, confirmLabel: "\u4FDD\u5B58", secondary: { label: '説明（任意）', placeholder: '検索の説明を入力してください', type: 'textarea' } })] }));
};
export default SearchPage;
